/*
 * The MIT License (MIT)
 *
 * Copyright (c) 2015 - present Instructure, Inc.
 *
 * Permission is hereby granted, free of charge, to any person obtaining a copy
 * of this software and associated documentation files (the "Software"), to deal
 * in the Software without restriction, including without limitation the rights
 * to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
 * copies of the Software, and to permit persons to whom the Software is
 * furnished to do so, subject to the following conditions:
 *
 * The above copyright notice and this permission notice shall be included in all
 * copies or substantial portions of the Software.
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
 * IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
 * FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
 * AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
 * LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
 * OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE
 * SOFTWARE.
 */

import React, { useContext } from 'react';
import PropTypes from 'prop-types';
import { ThemeProvider } from '@emotion/react';
import { TextDirectionContext } from '@instructure/ui-i18n';
import { DeterministicIdContextProvider, getElementType } from '@instructure/ui-react-utils';
import { getTheme } from '../getTheme';
/**
 * ---
 * category: components/utilities
 * ---
 * @module InstUISettingsProvider
 * @tsProps
 */
function InstUISettingsProvider(_ref) {
  let children = _ref.children,
    _ref$theme = _ref.theme,
    theme = _ref$theme === void 0 ? {} : _ref$theme,
    dir = _ref.dir,
    instanceCounterMap = _ref.instanceCounterMap,
    as = _ref.as;
  const finalDir = dir || useContext(TextDirectionContext);
  if (process.env.NODE_ENV !== 'production' && finalDir === 'auto') {}
  let providers = /*#__PURE__*/React.createElement(DeterministicIdContextProvider, {
    instanceCounterMap: instanceCounterMap
  }, /*#__PURE__*/React.createElement(ThemeProvider, {
    theme: getTheme(theme)
  }, /*#__PURE__*/React.createElement(TextDirectionContext.Provider, {
    value: finalDir
  }, children)));
  if (dir) {
    const Element = getElementType(InstUISettingsProvider, {
      as
    });
    providers = /*#__PURE__*/React.createElement(Element, {
      dir: finalDir
    }, providers);
  } else if (as && process.env.NODE_ENV !== 'production') {}
  return providers;
}
InstUISettingsProvider.propTypes = {
  /* eslint-disable react/require-default-props */
  children: PropTypes.node,
  theme: PropTypes.object,
  dir: PropTypes.oneOf(['ltr', 'rtl']),
  instanceCounterMap: PropTypes.instanceOf(Map),
  as: PropTypes.string
  /* eslint-enable react/require-default-props */
};

InstUISettingsProvider.defaultProps = {
  theme: {},
  as: void 0
};
export default InstUISettingsProvider;
export { InstUISettingsProvider };